import * as THREE from 'three';

/**
 * Opt-in render profiler: append `?perf=1` to the URL.
 *
 * FPS alone can't tell you WHY the GPU is busy, so this reports the numbers that
 * actually decide the cost of a frame:
 *  - draw calls / triangles: `renderer.info.render`, which counts BOTH the main
 *    pass and the shadow pass. A big gap between "shadows on" and "shadows off"
 *    frames means the shadow map is the bill, not the scene.
 *  - programs: distinct compiled shaders. This jumps when the light count changes
 *    (every point light re-specialises every material), and a rising count during
 *    play means shaders are compiling mid-frame — a classic stutter source.
 *  - lights: how many real (per-pixel) lights the fragment shaders are looping.
 *  - draw%: share of recent frames that actually issued a draw, i.e. how well the
 *    on-demand renderer in App.frame() is doing its job. Idle should be low.
 *
 * The overlay reads counters the renderer already maintains, so leaving it off
 * costs nothing and turning it on doesn't change what is drawn.
 */
export class PerfOverlay {
  private readonly root: HTMLDivElement;
  private readonly el: HTMLPreElement;
  private readonly copyBtn: HTMLButtonElement;
  private frames = 0;
  private drawn = 0;
  private lastFlush = performance.now();
  /** Peak draw calls seen this window — shadow-pass frames are the spikes. */
  private peakCalls = 0;

  constructor() {
    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'fixed', bottom: '8px', left: '8px', zIndex: '30',
      font: '11px/1.45 ui-monospace, Consolas, monospace',
      color: '#b9f6c4', background: 'rgba(0,0,0,0.66)',
      padding: '6px 9px', borderRadius: '5px',
      pointerEvents: 'none', textShadow: '0 1px 0 #000',
    });

    this.el = document.createElement('pre');
    Object.assign(this.el.style, { margin: '0', whiteSpace: 'pre', pointerEvents: 'none' });
    this.root.appendChild(this.el);

    this.copyBtn = document.createElement('button');
    this.copyBtn.textContent = 'copy';
    Object.assign(this.copyBtn.style, {
      marginTop: '4px', font: 'inherit', color: 'inherit',
      background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)',
      borderRadius: '3px', padding: '1px 6px', cursor: 'pointer', pointerEvents: 'auto',
    });
    this.copyBtn.addEventListener('click', () => {
      navigator.clipboard.writeText(this.el.textContent ?? '').then(() => {
        const prev = this.copyBtn.textContent;
        this.copyBtn.textContent = 'copied!';
        setTimeout(() => { this.copyBtn.textContent = prev; }, 900);
      });
    });
    this.root.appendChild(this.copyBtn);

    document.body.appendChild(this.root);
  }

  /** True when `?perf=1` (or `#perf`) is present. */
  static requested(): boolean {
    if (typeof location === 'undefined') return false;
    return new URLSearchParams(location.search).get('perf') === '1' || location.hash === '#perf';
  }

  /**
   * Call once per frame loop iteration, AFTER the (possibly skipped) draw.
   * `didDraw` distinguishes a real frame from one the on-demand renderer skipped.
   */
  sample(renderer: THREE.WebGLRenderer, scene: THREE.Scene, didDraw: boolean): void {
    this.frames++;
    if (didDraw) {
      this.drawn++;
      this.peakCalls = Math.max(this.peakCalls, renderer.info.render.calls);
    }

    const now = performance.now();
    const span = now - this.lastFlush;
    if (span < 500) return; // refresh twice a second; reading it every frame is noise

    const r = renderer.info.render;
    const m = renderer.info.memory;
    let lights = 0;
    let shadowCasters = 0;
    scene.traverse((o) => {
      if ((o as THREE.Light).isLight && (o as THREE.Light).visible) lights++;
      if ((o as THREE.Mesh).isMesh && o.castShadow) shadowCasters++;
    });

    const fps = (this.frames * 1000) / span;
    const drawPct = (this.drawn / Math.max(1, this.frames)) * 100;
    this.el.textContent = [
      `fps        ${fps.toFixed(0)}  (drew ${drawPct.toFixed(0)}% of frames)`,
      `calls      ${r.calls}   peak ${this.peakCalls}`,
      `triangles  ${r.triangles.toLocaleString()}`,
      `programs   ${renderer.info.programs?.length ?? 0}`,
      `lights     ${lights}   shadow casters ${shadowCasters}`,
      `geometries ${m.geometries}   textures ${m.textures}`,
      `pixelRatio ${renderer.getPixelRatio()}`,
    ].join('\n');

    this.frames = 0;
    this.drawn = 0;
    this.peakCalls = 0;
    this.lastFlush = now;
  }

  dispose(): void {
    this.root.remove();
  }
}
